import { api } from '@/services';
import { DDNSActionName, DDNSRecord, DDNSRecordAction } from '@tools-set/shared-types';
import { useCallback, useEffect, useState } from 'react';

export default function DdnsStatus() {
        const [records, setRecords] = useState<DDNSRecord[]>([]);
        const [loading, setLoading] = useState(true);

        const handleRecordAction = (action: DDNSRecordAction, record: DDNSRecord) => {
                console.log('Action triggered:', action, 'for record:', record);
                if (action.actionName === DDNSActionName.Update) {
                        api.updateCloudflareRecord(record.id).then(() => {
                                fetchStatus();
                        });
                }
        };

        const fetchStatus = useCallback(() => {
                api.fetchDDNSStatus().then((resp) => {
                        const { data } = resp || {};
                        const { ddnsRecords } = data || {};
                        setRecords(ddnsRecords || []);
                        setLoading(false);
                });
        }, []);

        useEffect(() => {
                fetchStatus();
        }, []);

	if (loading) {
		return (
			<div className="card-gradient border border-gray-100 rounded-xl p-5 w-full max-w-2xl shadow-soft">
				<div className="space-y-3">
					<div className="h-6 bg-gray-200 rounded w-1/3 animate-pulse"></div>
					<div className="h-20 bg-gray-200 rounded animate-pulse"></div>
					<div className="h-20 bg-gray-200 rounded animate-pulse"></div>
				</div>
			</div>
		);
	}

	const getStatusBadge = (status: DDNSRecord['status']) => {
		const styles = {
			active: 'bg-gradient-to-r from-green-100 to-green-50 text-green-700 border border-green-200',
			inactive: 'bg-gradient-to-r from-gray-100 to-gray-50 text-gray-700 border border-gray-200',
			error: 'bg-gradient-to-r from-red-100 to-red-50 text-red-700 border border-red-200',
		};
		const labels = {
			active: '✅',
			inactive: '⏸️',
			error: '❌',
		};
		return (
			<span className={`px-2.5 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1 ${styles[status]}`}>
				<span>{labels[status]}</span>
				<span className="capitalize">{status}</span>
			</span>
		);
	};

	const formatTime = (isoString: string) => {
                const date = new Date(isoString);
                const now = new Date();
                const diffMs = now.getTime() - date.getTime();
                const diffMins = Math.floor(diffMs / 60000);

                if (diffMins < 1) return '刚刚';
                if (diffMins < 60) return `${diffMins}分钟前`;
                const diffHours = Math.floor(diffMins / 60);
                if (diffHours < 24) return `${diffHours}小时前`;
                return date.toLocaleDateString('zh-CN');
        };

	return (
		<div className="card-gradient border border-gray-100 rounded-xl p-4 md:p-5 w-full max-w-2xl shadow-soft">
			{/* 卡片标题 */}
			<div className="mb-4 pb-3 border-b border-gray-100">
				<h2 className="text-base md:text-lg font-semibold text-gray-900 flex items-center gap-2">
					<span>🌐</span>
					DDNS 状态
				</h2>
			</div>

			{/* DNS 记录列表 */}
			<div className="space-y-3">
				{records.map((record) => (
					<div 
						key={record.id} 
						className="p-3 rounded-xl border border-gray-100 hover:border-primary-200 hover:bg-primary-50/30 transition-all duration-200"
					>
						{/* 记录头部 */}
						<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
							<div className="flex items-center gap-2">
								<h3 className="text-sm font-semibold text-gray-900 break-all">{record.name}</h3>
								{getStatusBadge(record.status)}
							</div>
							<span className="text-xs sm:text-sm text-gray-500">{formatTime(record.updatedAt)}</span>
						</div>

						{/* 信息展示 */}
						<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
							<div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 text-xs sm:text-sm text-gray-600">
								<span className="font-mono break-all">{record.value}</span>
								<div className="flex items-center gap-2">
									<span className="hidden sm:inline">·</span>
									<span>{record.provider}</span>
									<span>·</span>
									<span>TTL {record.ttl}s</span>
								</div>
							</div>

							{/* 操作按钮 - 由数据控制 */}
							{record.actions && record.actions.length > 0 && (
								<div className="flex items-center gap-2 mt-2 sm:mt-0">
									{record.actions.map((action, index) => (
										<button
											key={index}
											onClick={() => handleRecordAction(action, record)}
											className={`px-3 py-1.5 text-xs sm:text-sm font-medium rounded-lg transition-all duration-300 active:scale-95 ${
												action.type === 'primary'
													? 'bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white shadow-sm hover:shadow-primary hover:-translate-y-0.5'
													: 'bg-gray-100 hover:bg-gray-200 text-gray-700 hover:shadow-sm'
											}`}
										>
											{action.label}
										</button>
									))}
								</div>
							)}
						</div>
					</div>
				))}
			</div>
		</div>
	);
}
